import depsloader from "../../../depsloader";

const SEARCH_URL = "https://api.screenwork.de/geocoder/search";
const REVERSE_URL = "https://api.screenwork.de/geocoder/reverse";
const REQUEST_TIMEOUT = 25000;

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.axiosRequest = null;
    this.key = this._readKey();
  }

  _readKey() {
    try {
      const el = document.querySelector("html");
      const k = el && el.dataset ? el.dataset.immoappGeocoderKey : "";
      return typeof k === "string" && k.trim() !== "" ? k : "";
    } catch (e) {
      return "";
    }
  }

  cancelAutocomplete() {
    if (this.axiosRequest != null) {
      this.axiosRequest.source.cancel();
    }
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    }

    const p = [];

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    // Fehlt jede Orts-/Straßenangabe, den Feature-Namen als Beschriftung
    // erhalten, ohne ihn als Stadt zu erfinden.
    if (p.length === 0 && g.name) {
      p.push(g.name);
    }

    if (g.state) {
      p.push(g.state);
    }

    if (g.country) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  _geoaddress(l) {
    const props = l && l.properties ? l.properties : {};
    const coords =
      l && l.geometry && Array.isArray(l.geometry.coordinates)
        ? l.geometry.coordinates
        : [];
    const lng = coords[0];
    const lat = coords[1];

    if (
      typeof lat !== "number" ||
      typeof lng !== "number" ||
      !isFinite(lat) ||
      !isFinite(lng) ||
      lat < -90 ||
      lat > 90 ||
      lng < -180 ||
      lng > 180
    ) {
      return null;
    }

    const geoaddress = {};
    const type = props.type || null;

    // Legacy-Fallback (alte Photon-Struktur ohne "type"): Straßenname aus name.
    if (!type && props.osm_key === "highway" && props.name && !props.street) {
      props.street = props.name;
    }

    // Straße und Hausnummer.
    if (props.street && props.housenumber) {
      geoaddress.street = props.street;
      geoaddress.number = props.housenumber;
    } else if (props.street) {
      geoaddress.street = props.street;
    } else if (type === "street" && props.name) {
      geoaddress.street = props.name;
    }

    // PLZ.
    if (props.postcode) {
      geoaddress.zip = props.postcode;
    }

    // Stadt und Bezirk. properties.type und district vorrangig auswerten,
    // osm_key/osm_value nur als Legacy-Fallback.
    if (props.city) {
      geoaddress.city = props.city;
      if (props.district && props.district !== props.city) {
        geoaddress.district = props.district;
      }
    } else if (type === "district") {
      if (props.district) {
        geoaddress.city = props.district;
      } else if (props.name) {
        geoaddress.city = props.name;
      }
    } else if (
      (type === "city" ||
        type === "town" ||
        type === "village" ||
        type === "locality") &&
      props.name
    ) {
      geoaddress.city = props.name;
    } else if (!type) {
      // Legacy-Fallback über osm_key/osm_value.
      if (props.postcode && props.name) {
        geoaddress.city = props.name;
        let isDistrict = false;
        if (props.osm_key === "boundary") {
          isDistrict = true;
        } else if (props.osm_key === "place" && props.osm_value === "suburb") {
          isDistrict = true;
        }
        if (isDistrict) {
          geoaddress.district = props.name;
        }
      } else if (props.name && !geoaddress.street) {
        geoaddress.city = props.name;
      }
    }

    // Weder Ort noch Straße ermittelt: Feature-Namen als Beschriftung bewahren.
    if (!geoaddress.city && !geoaddress.street && props.name) {
      geoaddress.name = props.name;
    }

    geoaddress.lat = lat;
    geoaddress.lng = lng;
    geoaddress.state = props.state;
    geoaddress.country = props.country;

    geoaddress.text = this._geoaddressText(geoaddress);

    return geoaddress;
  }

  geocode(params) {
    return new Promise((resolve) => {
      if (!this.key) {
        resolve([]);
        return;
      }

      const CancelToken = this.axios.CancelToken;
      this.axiosRequest = {
        source: CancelToken.source()
      };

      params.set("key", this.key);

      this.axios
        .get(SEARCH_URL, {
          cancelToken: this.axiosRequest.source.token,
          timeout: REQUEST_TIMEOUT,
          params
        })
        .then((res) => {
          const results = [];
          const data = res && res.data;

          if (data && Array.isArray(data.features)) {
            data.features.forEach((l) => {
              const geoaddress = this._geoaddress(l);
              if (geoaddress) {
                results.push(geoaddress);
              }
            });
          }

          resolve(results);
        })
        .catch(() => {
          // Abbruch, Timeout oder Fehler: kontrolliert leer beenden, keine
          // dauerhaft offene Promise.
          resolve([]);
        })
        .finally(() => {
          this.axiosRequest = null;
        });
    });
  }

  autocomplete(q) {
    const params = new URLSearchParams();
    params.append("q", q);
    params.append("lang", "de");
    params.append("limit", "10");
    params.append("layer", "city");
    params.append("layer", "district");

    return this.geocode(params);
  }

  reverse(lat, lng) {
    return new Promise((resolve) => {
      const latNum = Number(lat);
      const lonNum = Number(lng);

      if (
        !this.key ||
        !isFinite(latNum) ||
        !isFinite(lonNum) ||
        latNum < -90 ||
        latNum > 90 ||
        lonNum < -180 ||
        lonNum > 180
      ) {
        resolve(null);
        return;
      }

      const params = new URLSearchParams();
      params.append("lat", String(latNum));
      params.append("lon", String(lonNum));
      params.append("lang", "de");
      params.append("key", this.key);

      this.axios
        .get(REVERSE_URL, {
          timeout: REQUEST_TIMEOUT,
          params
        })
        .then((res) => {
          const data = res && res.data;
          if (data && Array.isArray(data.features) && data.features.length > 0) {
            resolve(this._geoaddress(data.features[0]));
            return;
          }
          resolve(null);
        })
        .catch(() => {
          resolve(null);
        });
    });
  }
}

function GeocoderFactory() {
  return new Promise((resolve) => {
    depsloader.load("axios").then((axios) => {
      resolve(new Geocoder(axios));
    });
  });
}

export { GeocoderFactory };

export default {};
